import { getDoc, doc } from "firebase/firestore";
import { db } from "../firebase/client";
import { uploadPatientDataUrl } from "../services/storage";
import { uploadPatientFile } from "../services/storage";
import { getPatientFileKey, isImagePatientFile, normalizePatientFiles } from "../services/patientFiles";
import { auth } from "../firebase/client";
import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { Patient, AppTab, ClinicalNote, createInitialClinicalRegistry, type PatientFile } from "../contexts/types";
import { findSimilarCases, type SimilarCaseMatch } from "../services/similarCases";
import PatientVoiceIntakeModal from "./PatientVoiceIntakeModal";
import { applyVoicePatientDraftToForm, type VoicePatientDraft } from "../services/patientVoiceIntake";
import { UsageLimitError } from "../services/usageLimits";
import { buildDoctorProfilePayload, normalizeDoctorProfile } from "../utils/doctorProfile";
import { normalizePortugueseEncoding } from "../utils/atlasText";
import { PremiumActionButton, PremiumEmptyState, PremiumGrid, PremiumInput, PremiumModuleCard, PremiumPageShell, PremiumSectionCard } from "./premium";
import { 
  User, 
  Calendar, 
  MapPin, 
  Activity, 
  Upload, 
  X, 
  ArrowRight, 
  Search, 
  Plus, 
  FileText, 
  BookOpen, 
  BrainCircuit, 
  Printer, 
  ChevronRight, 
  Trash2, 
  Paperclip, 
  Clock, 
  Maximize2, 
  ZoomIn, 
  CheckCircle2, 
  AlertCircle, 
  FileSignature, 
  Save, 
  Check, 
  Mail, 
  Phone, 
  CreditCard, 
  Scale, 
  Ruler, 
  Edit,
  Stethoscope,
  ShieldAlert,
  Mic
} from 'lucide-react';

interface PatientModuleProps {
  onNavigateToTab: (tab: AppTab) => void;
  currentUser: string;
}

type DoctorData = {
  name: string;
  crm: string;
  crmState?: string;
  rqe: string;
  specialty?: string;
  subspecialty?: string;
  orthoId?: string;
  gender?: string;
  sexo?: string;
};

const hasDisplayValue = (value?: string | number | null) => {
  if (typeof value === "number") return value > 0;
  return !!String(value || "").trim();
};

const escapeHtml = (value?: string | number | null) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const legacyClinicalNoteTitleKeyMap: Record<string, string> = {
  "abertura de prontuário": "chartOpened",
  "medical chart opened": "chartOpened",
  "apertura del expediente": "chartOpened",
};

const legacyClinicalNoteContentKeyMap: Record<string, string> = {
  "paciente cadastrado.": "patientRegistered",
  "paciente cadastrado": "patientRegistered",
  "patient registered.": "patientRegistered",
  "patient registered": "patientRegistered",
  "paciente registrado.": "patientRegistered",
  "paciente registrado": "patientRegistered",
};

const inferLegacyClinicalNoteKey = (
  value: string | undefined,
  map: Record<string, string>
) => map[(value || "").trim().toLowerCase()];

const languageLocaleMap = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
} as const;

const comorbidityTranslationKeyMap: Record<string, string> = {
  Diabetes: "patientComorbidityDiabetes",
  "Tabagismo (Smoking)": "patientComorbiditySmoking",
  Osteoporose: "patientComorbidityOsteoporosis",
  Hipertens\u00e3o: "patientComorbidityHypertension",
  Obesidade: "patientComorbidityObesity",
  Coagulopatia: "patientComorbidityCoagulopathy",
};

const allergyTranslationKeyMap: Record<string, string> = {
  Dipirona: "patientAllergyDipyrone",
  "AAS / Aspirina": "patientAllergyAspirin",
  Penicilina: "patientAllergyPenicillin",
  Sulfa: "patientAllergySulfa",
  "Iodo (Contraste)": "patientAllergyIodineContrast",
  L\u00e1tex: "patientAllergyLatex",
  AINEs: "patientAllergyNsaids",
};

const anatomyTranslationKeyMap: Record<string, string> = {
  "Clavícula": "patientAnatomyClavicle",
  "Ombro": "patientAnatomyShoulderProximalHumerus",
  "Braço": "patientAnatomyHumeralShaft",
  "Cotovelo": "patientAnatomyElbow",
  "Antebraço": "patientAnatomyForearm",
  "Punho": "patientAnatomyDistalRadius",
  "Mão": "patientAnatomyHand",
  "Coluna cervical": "patientAnatomyCervicalSpine",
  "Coluna torácica": "patientAnatomyThoracicSpine",
  "Coluna lombar": "patientAnatomyLumbarSpine",
  "Pelve": "patientAnatomyPelvis",
  "Quadril": "patientAnatomyHipAcetabulum",
  "Coxa": "patientAnatomyFemoralShaft",
  "Joelho": "patientAnatomyKnee",
  "Perna": "patientAnatomyTibialShaft",
  "Tornozelo": "patientAnatomyAnkle",
  "Pé": "patientAnatomyFootCalcaneus",
};

const anatomyLegacyLabelEntries = [
  ["clavicula", "Clavícula"],
  ["ombro (proximal humerus)", "Ombro"],
  ["ombro (úmero proximal)", "Ombro"],
  ["ombro", "Ombro"],
  ["diáfise umeral", "Braço"],
  ["diafise umeral", "Braço"],
  ["cotovelo", "Cotovelo"],
  ["rádio distal", "Punho"],
  ["radio distal", "Punho"],
  ["punho (escafoide)", "Punho"],
  ["mão", "Mão"],
  ["mao", "Mão"],
  ["coluna cervical", "Coluna cervical"],
  ["coluna torácica", "Coluna torácica"],
  ["coluna toracica", "Coluna torácica"],
  ["coluna lombar", "Coluna lombar"],
  ["pelve", "Pelve"],
  ["quadril (acetábulo)", "Quadril"],
  ["quadril (acetabulo)", "Quadril"],
  ["quadril", "Quadril"],
  ["fêmur proximal", "Quadril"],
  ["femur proximal", "Quadril"],
  ["fêmur diáfise", "Coxa"],
  ["femur diafise", "Coxa"],
  ["joelho", "Joelho"],
  ["platô tibial", "Joelho"],
  ["plato tibial", "Joelho"],
  ["tíbia diáfise", "Perna"],
  ["tibia diafise", "Perna"],
  ["perna", "Perna"],
  ["tornozelo", "Tornozelo"],
  ["pé (calcâneo)", "Pé"],
  ["pé (calcaneo)", "Pé"],
  ["pe (calcaneo)", "Pé"],
  ["calcâneo", "Pé"],
  ["calcaneo", "Pé"],
  ["pe", "Pé"],
  ["braco", "Braço"],
  ["antebraco", "Antebraço"],
  ["coxa", "Coxa"],
  ["coluna toracica", "Coluna torácica"],
] as const;

const anatomyLegacyLabelMap = Object.fromEntries(anatomyLegacyLabelEntries) as Record<string, string>;

const anatomyOptions = [
  "Clavícula",
  "Ombro",
  "Braço",
  "Cotovelo",
  "Antebraço",
  "Punho",
  "Mão",
  "Coluna cervical",
  "Coluna torácica",
  "Coluna lombar",
  "Pelve",
  "Quadril",
  "Coxa",
  "Joelho",
  "Perna",
  "Tornozelo",
  "Pé",
] as const;

const anatomyLookupKey = (value?: string) =>
  normalizePortugueseEncoding(String(value || ""))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const normalizeAnatomyValue = (value?: string) => {
  const lookupKey = anatomyLookupKey(value);
  if (!lookupKey) return "";

  return anatomyLegacyLabelMap[lookupKey] || String(value || "").trim();
};

const createEmptyPatientForm = (): Partial<Patient> => ({
  name: '',
  age: '',
  sex: 'M',
  side: 'Right',
  anatomy: '',
  comorbidities: [],
  images: [],
  status: 'Pre-op',
  cpf: '',
  phone: '',
  email: '',
  weight: '',
  height: '',
  bmi: '',
  allergies: [],
  chiefComplaint: '',
  historyOfPresentIllness: '',
  painLocation: '',
  symptomDuration: '',
  traumaMechanism: '',
  functionalLimitation: '',
  physicalExam: '',
  profession: '',
  suspectedDiagnosis: '',
  initialPlan: '',
  notes: '',
});

type AnatomyDropdownProps = {
  value?: string;
  options: readonly string[];
  placeholder: string;
  onChange: (value: string) => void;
  translateOption: (value: string) => string;
};

const AnatomyDropdown: React.FC<AnatomyDropdownProps> = ({
  value,
  options,
  placeholder,
  onChange,
  translateOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const normalizedValue = normalizeAnatomyValue(value);
  const selectedLabel = normalizedValue ? translateOption(normalizedValue) : placeholder;

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-2xl border border-cyan-400/20 bg-slate-950/85 px-4 py-3.5 text-left text-sm text-white outline-none transition hover:border-cyan-300/35 hover:bg-slate-900 focus:border-cyan-300/45"
      >
        <span className={normalizedValue ? 'text-white' : 'text-slate-500'}>{selectedLabel}</span>
        <ChevronRight className={`h-4 w-4 shrink-0 text-cyan-200 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-[80] mt-2 overflow-hidden rounded-2xl border border-cyan-400/20 bg-slate-950/98 shadow-[0_22px_70px_rgba(0,0,0,0.45)] backdrop-blur">
          <div className="max-h-72 overflow-y-auto p-2">
            {options.map((option) => {
              const active = normalizedValue === option;

              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm transition ${
                    active
                      ? 'bg-cyan-500/15 text-cyan-100 shadow-[inset_0_0_0_1px_rgba(103,232,249,0.12)]'
                      : 'text-slate-200 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span>{translateOption(option)}</span>
                  {active && <Check className="h-4 w-4 text-cyan-300" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PatientModule: React.FC<PatientModuleProps> = ({ onNavigateToTab, currentUser }) => {
  const { t, language } = useLanguage();
  const { patients, addPatient, activePatient, setActivePatient, deletePatient, updatePatient } = usePatient();
  const getPatientStatusLabel = (status?: string) =>
    status === 'Urgency' ? t('patientStatusUrgency') : t('patientStatusUnderReview');
  const getUsageErrorMessage = (error: unknown) => {
    if (error instanceof UsageLimitError) {
      const featureLabel =
        error.metric === 'patients'
          ? t('usagePatientsLabel')
          : error.metric === 'ocrMonthly'
          ? t('usageOcrMonthlyLabel')
          : error.metric === 'threeDMonthly'
          ? t('usageThreeDMonthlyLabel')
          : t('usageAnalysesMonthlyLabel');

      return t('usageLimitReachedMessage', {
        feature: featureLabel.toLowerCase(),
        plan: error.tier.toUpperCase(),
        limit:
          error.limit == null ? t('usageUnlimited') : String(error.limit),
      });
    }

    return error instanceof Error ? error.message : t('patientSaveError');
  };
  const currentLocale = languageLocaleMap[language];
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'list' | 'form'>('list');
  const [isEditing, setIsEditing] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
const [isDraggingImage, setIsDraggingImage] = useState(false);
const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [doctorData, setDoctorData] = useState<DoctorData | null>(null);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [similarCases, setSimilarCases] = useState<SimilarCaseMatch[]>([]);
  const [loadingSimilarCases, setLoadingSimilarCases] = useState(false);
  const [showClinicalReport, setShowClinicalReport] = useState(false);
  const [showVoiceIntakeModal, setShowVoiceIntakeModal] = useState(false);
  const [patientHubTab, setPatientHubTab] = useState<'evolution' | 'exams' | 'surgical' | 'documents' | 'ai'>('evolution');

useEffect(() => {
  const loadUserPlan = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const snap = await getDoc(doc(db, "users", uid));

    if (snap.exists()) {
      const data = snap.data();
      setUserPlan(data.plan?.tier || "free");
    }
  };

  loadUserPlan();
}, []);

  const [hoveredPatientImages, setHoveredPatientImages] = useState<string[] | null>(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const [isPatientUploadDragging, setIsPatientUploadDragging] = useState(false);
  const [patientUploadToast, setPatientUploadToast] = useState<{ id: number; type: 'success' | 'error'; message: string } | null>(null);
  const patientUploadToastTimerRef = useRef<number | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeFileInputRef = useRef<HTMLInputElement>(null);
  const intelligentReportRef = useRef<HTMLDivElement>(null);

  // Form State
  const [formData, setFormData] = useState<Partial<Patient>>(createEmptyPatientForm());

  const comorbidityOptions = [
    "Diabetes", "Tabagismo (Smoking)", "Osteoporose", "Hipertensão", "Obesidade", "Coagulopatia"
  ];

  const allergyOptions = [
    "Dipirona", "AAS / Aspirina", "Penicilina", "Sulfa", "Iodo (Contraste)", "Látex", "AINEs"
  ];

  const showPatientUploadToast = (type: 'success' | 'error', message: string) => {
    if (patientUploadToastTimerRef.current) {
      window.clearTimeout(patientUploadToastTimerRef.current);
    }

    setPatientUploadToast({ id: Date.now(), type, message });
    patientUploadToastTimerRef.current = window.setTimeout(() => {
      setPatientUploadToast(null);
      patientUploadToastTimerRef.current = null;
    }, 3200);
  };

  useEffect(() => {
    return () => {
      if (patientUploadToastTimerRef.current) {
        window.clearTimeout(patientUploadToastTimerRef.current);
      }
    };
  }, []);

  // Auto-calculate BMI effect
useEffect(() => {
  if (formData.weight && formData.height) {
    const w = parseFloat(formData.weight.replace(',', '.'));
    const h = parseFloat(formData.height.replace(',', '.'));
    if (w > 0 && h > 0) {
      const imc = w / (h * h);
      setFormData(prev => ({
        ...prev,
        bmi: imc.toFixed(2),
      }));
    }
  }
}, [formData.weight, formData.height]);

// Categoria do IMC (Adicionando uma classificação visual)
const imcCategory = parseFloat(formData.bmi || '0') >= 30
  ? 'Obesidade'
  : parseFloat(formData.bmi || '0') >= 25
  ? 'Sobrepeso'
  : parseFloat(formData.bmi || '0') >= 18.5
  ? 'Peso Normal'
  : 'Baixo Peso';

  // Load Doctor Data
  const refreshDoctorData = async () => {
    const uid = auth.currentUser?.uid;
    const email = auth.currentUser?.email || '';

    if (uid) {
      try {
        const snap = await getDoc(doc(db, "users", uid));

        if (snap.exists()) {
          const data = snap.data() as any;
          const profile = data?.profile || {};

          const normalizedDoctor = buildDoctorProfilePayload(
            normalizeDoctorProfile(data, profile, auth.currentUser)
          );

          setDoctorData({
            name: normalizedDoctor.fullName || auth.currentUser?.displayName || '',
            crm: normalizedDoctor.crm || '',
            crmState: normalizedDoctor.crmState || '',
            rqe: normalizedDoctor.rqe || '',
            specialty: normalizedDoctor.specialty,
            subspecialty: normalizedDoctor.subspecialty || '',
            orthoId: normalizedDoctor.orthoId || data.username || email || uid,
            gender: profile.gender || data.gender || undefined,
            sexo: profile.sexo || data.sexo || undefined,
          });
          return;
        }
      } catch (error) {
        console.error('Failed to load doctor data from Firestore:', error);
      }
    }

    if (currentUser) {
      const usersStr = localStorage.getItem('orthoprecision_users');
      const users = usersStr ? JSON.parse(usersStr) : [];
      const found = users.find((u: any) => u.username === currentUser || u.email === currentUser);

      if (found) {
        const normalizedDoctor = buildDoctorProfilePayload(
          normalizeDoctorProfile(found, found?.profile)
        );

        setDoctorData({
          name: normalizedDoctor.fullName || found.name,
          crm: normalizedDoctor.crm || found.crm || '',
          crmState: normalizedDoctor.crmState || found.crmState || '',
          rqe: normalizedDoctor.rqe || found.rqe || '',
          specialty: normalizedDoctor.specialty,
          subspecialty: normalizedDoctor.subspecialty || '',
          orthoId: normalizedDoctor.orthoId || found.orthoId || found.username || found.email || uid || undefined,
          gender: found.gender || undefined,
          sexo: found.sexo || undefined,
        });
      } else if (currentUser === 'admin') {
        setDoctorData({
          name: "Administrador do Sistema",
          crm: "000000/SP",
          crmState: "SP",
          rqe: "00000",
          specialty: "Médico",
          subspecialty: "",
          orthoId: 'admin',
          gender: 'male',
          sexo: 'masculino',
        });
      }
    }
  };

  useEffect(() => {
    void refreshDoctorData();
  }, [currentUser]);

  // Refresh when returning to dashboard
  useEffect(() => {
    if (view === 'list' && activePatient) {
      void refreshDoctorData();
    }
  }, [view, activePatient]);

  useEffect(() => {
    setPatientHubTab('evolution');
  }, [activePatient?.id]);

  const emailFallbackSource =
    auth.currentUser?.email || (currentUser.includes('@') ? currentUser : '');
  const emailFallbackName = emailFallbackSource
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .trim();
  const welcomeNameSource =
    doctorData?.name?.trim() ||
    auth.currentUser?.displayName?.trim() ||
    emailFallbackName;
  const inferredGenderFromName =
    /^dra\.?\s*/i.test(welcomeNameSource || '')
      ? 'female'
      : /^dr\.?\s*/i.test(welcomeNameSource || '')
      ? 'male'
      : '';
  const normalizedGender = String(
    doctorData?.gender ||
      doctorData?.sexo ||
      inferredGenderFromName ||
      ''
  )
    .trim()
    .toLowerCase();
  const normalizedDoctorName = (welcomeNameSource || '')
    .replace(/^dr\.?\s*/i, '')
    .replace(/^dra\.?\s*/i, '')
    .trim();
  const welcomeHeading = normalizedDoctorName
    ? t('patientWelcome', { name: normalizedDoctorName })
    : t('patientWelcomeFallback');

  const activePatientDoctor = activePatient
    ? ((activePatient as any).doctor || activePatient.responsibleDoctor || null)
    : null;
  const activePatientAttachments = normalizePatientFiles(
    activePatient?.patientFiles,
    activePatient?.images,
    activePatient?.createdAt
  );
  const activePatientImageUrls = activePatientAttachments
    .filter((file) => isImagePatientFile(file))
    .map((file) => file.url);
  const activePatientComorbidities = Array.isArray(activePatient?.comorbidities) ? activePatient.comorbidities : [];
  const activePatientClinicalNotes = Array.isArray(activePatient?.clinicalNotes) ? activePatient.clinicalNotes : [];
  const patientDoctorOrthoId = activePatient
    ? ((activePatient as any).doctorOrthoId ||
        (activePatient as any).doctor?.orthoId ||
        activePatient.responsibleDoctor?.orthoId ||
        (activePatient as any).responsibleDoctorUid)
    : undefined;
  const shouldUseLoggedDoctorData =
    !!activePatient &&
    !!doctorData?.orthoId &&
    !!patientDoctorOrthoId &&
    doctorData.orthoId === patientDoctorOrthoId;
  const responsibleDoctorInfo = shouldUseLoggedDoctorData
    ? { ...activePatientDoctor, ...doctorData }
    : activePatientDoctor || doctorData;

  if (typeof window !== "undefined") {
    console.log("[PatientModule] render diagnostics", {
      route: window.location.pathname,
      activePatientId: activePatient?.id || null,
      files: activePatientAttachments.length,
      images: activePatientImageUrls.length,
      comorbidities: activePatientComorbidities.length,
      clinicalNotes: activePatientClinicalNotes.length,
    });
  }

  const getMostCommonValue = (values: string[]) => {
    const counts = values.reduce((acc, value) => {
      const normalized = value.trim();
      if (!normalized) return acc;
      acc[normalized] = (acc[normalized] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const [mostCommon] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return mostCommon?.[0] || '';
  };

  const mostCommonTreatmentStrategy = getMostCommonValue(
    similarCases.map((item) => item.treatmentStrategy)
  );
  const mostCommonFixationType = getMostCommonValue(
    similarCases.map((item) => item.fixationType)
  );
  const unionCasesWithFollowUp = similarCases.filter(
    (item) =>
      (item.healingStatus || "").trim().toLowerCase() === "union" &&
      typeof item.followUpMonths === "number" &&
      item.followUpMonths > 0
  );
  const averageHealingWeeks =
    unionCasesWithFollowUp.length >= 3
      ? Math.round(
          unionCasesWithFollowUp.reduce(
            (sum, item) => sum + (item.followUpMonths || 0) * 4.3,
            0
          ) / unionCasesWithFollowUp.length
        )
      : null;
  const complicationCasesCount = similarCases.filter((item) => {
    const value = (item.complications || "").trim().toLowerCase();
    return !!value && value !== "none";
  }).length;
  const complicationRate =
    similarCases.length >= 5
      ? Math.round((complicationCasesCount / similarCases.length) * 100)
      : null;
  const mostCommonImplantType = getMostCommonValue(
    similarCases.map((item) => item.implantType || "")
  );
  const strategyBuckets = similarCases.reduce((acc, item) => {
      const strategy = (item.treatmentStrategy || '').trim();
      if (!strategy) return acc;

      if (!acc[strategy]) {
        acc[strategy] = {
          strategy,
          cases: 0,
          complications: 0,
        };
      }

      acc[strategy].cases += 1;

      const complicationValue = (item.complications || '').trim().toLowerCase();
      if (complicationValue && complicationValue !== 'none') {
        acc[strategy].complications += 1;
      }

      return acc;
    }, {} as Record<string, { strategy: string; cases: number; complications: number }>);
  const strategyComparison = (Object.values(strategyBuckets) as Array<{
    strategy: string;
    cases: number;
    complications: number;
  }>)
    .filter((item) => item.cases >= 2)
    .map((item) => ({
      ...item,
      complicationRate: Math.round((item.complications / item.cases) * 100),
    }))
    .sort((a, b) => b.cases - a.cases);

  const reportDate = new Date();
  const reportDateLabel = reportDate.toLocaleDateString(currentLocale);
  const reportDateTimeLabel = reportDate.toLocaleString(currentLocale);
  const caseIdentifier = activePatient?.id || "";
  const doctorOrthoId =
    responsibleDoctorInfo?.orthoId ||
    activePatient?.doctorOrthoId ||
    activePatient?.responsibleDoctor?.orthoId ||
    "";
  const diagnosisBone =
    activePatient?.clinicalRegistry?.diagnosis.bone ||
    activePatient?.caseFingerprint?.bone ||
    activePatient?.anatomy ||
    "";
  const classificationSystem =
    activePatient?.clinicalRegistry?.diagnosis.classificationSystem || "";
  const classificationCode =
    activePatient?.clinicalRegistry?.diagnosis.classificationCode ||
    activePatient?.caseFingerprint?.classification ||
    "";
  const injuryMechanism =
    activePatient?.clinicalRegistry?.diagnosis.injuryMechanism || "";
  const treatmentStrategy =
    activePatient?.clinicalRegistry?.treatment.strategy ||
    activePatient?.caseFingerprint?.treatmentStrategy ||
    "";
  const implantType = activePatient?.clinicalRegistry?.treatment.implantType || "";
  const fixationType =
    activePatient?.clinicalRegistry?.treatment.fixationType ||
    activePatient?.caseFingerprint?.fixationType ||
    "";
  const procedureType = activePatient?.clinicalRegistry?.procedure.procedureType || "";
  const procedureDate = activePatient?.clinicalRegistry?.procedure.procedureDate || "";
  const healingStatus = activePatient?.clinicalRegistry?.outcome.healingStatus || "";
  const complicationsValue = activePatient?.clinicalRegistry?.outcome.complications || "";
  const normalizedComplicationsValue = complicationsValue.trim().toLowerCase();
  const complicationsDisplay = !complicationsValue.trim()
    ? ""
    : normalizedComplicationsValue === "none"
    ? t('patientNoneRegistered')
    : complicationsValue;
  const followUpMonths =
    typeof activePatient?.clinicalRegistry?.outcome?.followUpMonths === "number" &&
    (activePatient.clinicalRegistry?.outcome?.followUpMonths ?? 0) > 0
      ? t('patientFollowUpMonths', { count: activePatient.clinicalRegistry?.outcome?.followUpMonths ?? 0 })
      : "";

  const getPatientSideShortLabel = (side: Patient["side"]) =>
    side === "Left"
      ? t('patientSideLeftShort')
      : side === "Right"
        ? t('patientSideRightShort')
        : t('patientSideBilateralShort');
  const getPatientSideFullLabel = (side: Patient["side"]) =>
    side === "Left"
      ? t('left')
      : side === "Right"
        ? t('right')
        : t('bilateral');
  const normalizeLegacyLabel = (value: string, legacyMap?: Record<string, string>) =>
    (legacyMap && legacyMap[value]) || value;
  const getPatientSexLabel = (sex: "M" | "F") => (sex === "M" ? t('male') : t('female'));
  const translateMappedLabel = (
    value: string,
    keyMap: Record<string, string>,
    legacyMap?: Record<string, string>
  ) => {
    const normalizedValue = value ? normalizeLegacyLabel(value, legacyMap) : "";
    return normalizedValue ? t(keyMap[normalizedValue] || normalizedValue) : "";
  };
  const translateComorbidity = (value: string) =>
    translateMappedLabel(value, comorbidityTranslationKeyMap);
  const translateAllergy = (value: string) =>
    translateMappedLabel(value, allergyTranslationKeyMap);
  const translateAnatomy = (value: string) =>
    translateMappedLabel(normalizeAnatomyValue(value), anatomyTranslationKeyMap);
  const translateList = (values: string[] | undefined, translator: (value: string) => string) =>
    values && values.length > 0 ? values.map((value) => translator(value)).join(", ") : t('patientNone');
  const getPatientNoteTypeLabel = (type: ClinicalNote['type']) => {
    if (type === 'Protocol') return t('patientNoteTypeProtocol');
    if (type === 'Consult') return t('patientNoteTypeConsult');
    if (type === 'Atlas') return t('patientNoteTypeSurgicalPlan');
    return t('patientNoteTypeDocument');
  };
  const getClinicalNoteTitleKey = (note: ClinicalNote) =>
    note.titleKey || inferLegacyClinicalNoteKey(note.title, legacyClinicalNoteTitleKeyMap);
  const getClinicalNoteContentKey = (note: ClinicalNote) =>
    note.contentKey || inferLegacyClinicalNoteKey(note.content, legacyClinicalNoteContentKeyMap);
  const isChartOpenRegistrationNote = (note: ClinicalNote) =>
    getClinicalNoteTitleKey(note) === 'chartOpened' &&
    getClinicalNoteContentKey(note) === 'patientRegistered';
  const reportAnalysisTitleMarkers = [
    'análise de laudo por ia',
    'ai report analysis',
    'análisis de informe por ia',
    'análise de laudo:',
    'report analysis:',
    'análisis de informe:',
  ];
  const reportAnalysisSourceMarkers = [
    'ocr / análise de laudo',
    'ocr / report analysis',
    'ocr / análisis de informe',
  ];
  const getPatientCaseToolProgress = (patient?: Patient | null) => {
    const completionOverrides = ((patient as any)?.caseToolCompletion ||
      (patient as any)?.toolCompletion ||
      {}) as Partial<Record<'decision' | 'consult' | 'atlas' | 'bureaucracy', boolean>>;
    const clinicalNotes = patient?.clinicalNotes || [];
    const hasClinicalNoteType = (type: ClinicalNote['type']) =>
      clinicalNotes.some((note) => note.type === type);
    const classificationValue =
      patient?.clinicalRegistry?.diagnosis.classificationCode ||
      patient?.caseFingerprint?.classification ||
      "";
    const treatmentValue =
      patient?.clinicalRegistry?.treatment.strategy ||
      patient?.caseFingerprint?.treatmentStrategy ||
      "";
    const decisionCompleted =
      completionOverrides.decision ??
      (hasClinicalNoteType('Protocol') ||
        hasDisplayValue(classificationValue) ||
        hasDisplayValue(treatmentValue));
    const consultCompleted =
      completionOverrides.consult ?? hasClinicalNoteType('Consult');
    const atlasCompleted =
      completionOverrides.atlas ?? hasClinicalNoteType('Atlas');
    const bureaucracyCompleted =
      completionOverrides.bureaucracy ??
      clinicalNotes.some((note) => {
        if (note.type === 'Consent') return true;
        if (note.type !== 'Document') return false;
        if (isChartOpenRegistrationNote(note)) return false;
        return hasDisplayValue(note.title) && hasDisplayValue(note.content);
      });
    const reportAnalysisCompleted = clinicalNotes.some((note) => {
      if (note.type !== 'Consult') return false;
      const normalizedTitle = String(note.title || '').trim().toLowerCase();
      const normalizedContent = String(note.content || '').trim().toLowerCase();
      return reportAnalysisTitleMarkers.some((marker) => normalizedTitle.includes(marker)) ||
        reportAnalysisSourceMarkers.some((marker) => normalizedContent.includes(marker));
    });
    const completedToolsCount = [
      decisionCompleted,
      consultCompleted,
      atlasCompleted,
      bureaucracyCompleted,
    ].filter(Boolean).length;

    return {
      decisionCompleted,
      consultCompleted,
      atlasCompleted,
      bureaucracyCompleted,
      reportAnalysisCompleted,
      completedToolsCount,
      analysisInProgress: completedToolsCount < 4,
    };
  };
  const {
    decisionCompleted: decisionToolCompleted,
    consultCompleted: consultToolCompleted,
    atlasCompleted: atlasToolCompleted,
    bureaucracyCompleted: bureaucracyToolCompleted,
    reportAnalysisCompleted: reportAnalysisToolCompleted,
    completedToolsCount,
  } = getPatientCaseToolProgress(activePatient);
  const caseToolsFullyCompleted = completedToolsCount === 4;
  const caseCompletedStatusLabel = t('patientCaseCompleted');
  const caseWorkflowStatusLabel = caseToolsFullyCompleted ? caseCompletedStatusLabel : t('patientUnderReview');
  const getClinicalNoteTitle = (note: ClinicalNote) => {
    const key = getClinicalNoteTitleKey(note);
    return normalizePortugueseEncoding(key ? t(key) : note.title);
  };
  const getClinicalNoteContent = (note: ClinicalNote) => {
    const key = getClinicalNoteContentKey(note);
    return normalizePortugueseEncoding(key ? t(key) : note.content);
  };

  const diagnosisRows = [
    { label: t('patientDiagnosisBone'), value: diagnosisBone },
    { label: t('patientDiagnosisClassificationSystem'), value: classificationSystem },
    { label: t('patientDiagnosisClassificationCode'), value: classificationCode },
    { label: t('patientDiagnosisInjuryMechanism'), value: injuryMechanism },
  ].filter((item) => hasDisplayValue(item.value));

  const treatmentRows = [
    { label: t('patientTreatmentStrategy'), value: treatmentStrategy },
    { label: t('patientTreatmentImplantType'), value: implantType },
    { label: t('patientTreatmentFixationType'), value: fixationType },
  ].filter((item) => hasDisplayValue(item.value));

  const procedureRows = [
    { label: t('patientProcedureType'), value: procedureType },
    { label: t('patientProcedureDate'), value: procedureDate },
  ].filter((item) => hasDisplayValue(item.value));

  const outcomeRows = [
    { label: t('patientOutcomeHealingStatus'), value: healingStatus },
    { label: t('patientOutcomeComplications'), value: complicationsDisplay },
    { label: t('patientOutcomeFollowUp'), value: followUpMonths },
  ].filter((item) => hasDisplayValue(item.value));

  const evidenceRows = [
    { label: t('similarCasesFound'), value: String(similarCases.length) },
    { label: t('patientMostFrequentStrategy'), value: mostCommonTreatmentStrategy },
    { label: t('patientMostFrequentImplant'), value: mostCommonImplantType },
    {
      label: t('patientAverageHealingTime'),
      value: averageHealingWeeks !== null ? t('patientHealingWeeks', { count: averageHealingWeeks }) : "",
    },
    {
      label: t('patientComplicationRate'),
      value: complicationRate !== null ? `${complicationRate}%` : "",
    },
  ].filter((item) => item.label === t('similarCasesFound') || hasDisplayValue(item.value));

  const diagnosisSummaryLabel = [diagnosisBone, classificationSystem, classificationCode]
    .filter((value) => hasDisplayValue(value))
    .join(" / ");
  const treatmentMaterialLabel = [implantType, fixationType]
    .filter((value) => hasDisplayValue(value))
    .join(" e ");

  const intelligentSummaryParts: string[] = [];

  if (diagnosisSummaryLabel) {
    intelligentSummaryParts.push(
      hasDisplayValue(injuryMechanism)
        ? t('patientSummaryDiagnosisWithMechanism', { diagnosis: diagnosisSummaryLabel, mechanism: injuryMechanism })
        : t('patientSummaryDiagnosis', { diagnosis: diagnosisSummaryLabel })
    );
  }

  if (treatmentStrategy || treatmentMaterialLabel) {
    if (treatmentStrategy) {
      intelligentSummaryParts.push(
        treatmentMaterialLabel
          ? t('patientSummaryTreatmentWithMaterials', { strategy: treatmentStrategy, materials: treatmentMaterialLabel })
          : t('patientSummaryTreatment', { strategy: treatmentStrategy })
      );
    } else {
      intelligentSummaryParts.push(
        t('patientSummaryMaterialsOnly', { materials: treatmentMaterialLabel })
      );
    }
  }

  if (similarCases.length > 0) {
    const evidenceSummaryParts = [
      averageHealingWeeks !== null ? t('patientSummaryAverageHealing', { count: averageHealingWeeks }) : "",
      complicationRate !== null ? t('patientSummaryComplicationRate', { rate: complicationRate }) : "",
      mostCommonTreatmentStrategy
        ? t('patientSummaryPredominance', { strategy: mostCommonTreatmentStrategy })
        : "",
    ].filter(Boolean);

    intelligentSummaryParts.push(
      evidenceSummaryParts.length > 0
        ? t('patientSummaryEvidence', { count: similarCases.length, details: evidenceSummaryParts.join(", ") })
        : t('patientSummaryEvidenceComparison', { count: similarCases.length })
    );
  }

  const intelligentSummary =
    intelligentSummaryParts.join(" ") ||
    t('patientSummaryInsufficientData');

  useEffect(() => {
    let isMounted = true;

    const loadSimilarCases = async () => {
      if (!activePatient?.id || !activePatient.caseFingerprint) {
        if (isMounted) {
          setSimilarCases([]);
          setLoadingSimilarCases(false);
        }
        return;
      }

      setLoadingSimilarCases(true);

      try {
        const matches = await findSimilarCases(activePatient.caseFingerprint, {
          currentCaseId: activePatient.id,
        });

        if (isMounted) {
          setSimilarCases(matches);
        }
      } catch (error) {
        console.error('Failed to load similar cases:', error);
        if (isMounted) {
          setSimilarCases([]);
        }
      } finally {
        if (isMounted) {
          setLoadingSimilarCases(false);
        }
      }
    };

    void loadSimilarCases();

    return () => {
      isMounted = false;
    };
  }, [activePatient?.id, activePatient?.caseFingerprint]);

  useEffect(() => {
    setShowClinicalReport(false);
  }, [activePatient?.id]);

  useEffect(() => {
    if (!activePatient?.id || activePatientClinicalNotes.length === 0) {
      return;
    }

    let changed = false;
    const normalizedNotes = activePatientClinicalNotes.map((note) => {
      console.log("[PatientModule] clinical note item", note);
      const inferredTitleKey = getClinicalNoteTitleKey(note);
      const inferredContentKey = getClinicalNoteContentKey(note);

      if (note.titleKey === inferredTitleKey && note.contentKey === inferredContentKey) {
        return note;
      }

      changed = true;
      return {
        ...note,
        titleKey: inferredTitleKey || note.titleKey,
        contentKey: inferredContentKey || note.contentKey,
      };
    });

    if (changed) {
      updatePatient(activePatient.id, { clinicalNotes: normalizedNotes });
    }
  }, [activePatient?.id, activePatient?.clinicalNotes]);

  function handleCreateNew() {
    setActivePatient(null);
    setFormData(createEmptyPatientForm());
    setIsEditing(false);
    setView('form');
  }

  const handleApplyVoiceDraft = (draft: VoicePatientDraft) => {
    const preserveEditingContext = view === 'form' && isEditing && !!activePatient;

    if (!preserveEditingContext) {
      setActivePatient(null);
      setIsEditing(false);
    }

    setView('form');
    setShowVoiceIntakeModal(false);
    setFormData((current) => applyVoicePatientDraftToForm(current, draft));
  };

const openImageViewer = (img: string) => {
  setFullScreenImage(img);
  setImageZoom(1);
  setImageOffset({ x: 0, y: 0 });
};

const closeImageViewer = () => {
  setFullScreenImage(null);
  setImageZoom(1);
  setImageOffset({ x: 0, y: 0 });
  setIsDraggingImage(false);
};

const zoomInImage = () => {
  setImageZoom((prev) => Math.min(prev + 0.25, 4));
};

const zoomOutImage = () => {
  setImageZoom((prev) => {
    const next = Math.max(prev - 0.25, 1);
    if (next === 1) {
      setImageOffset({ x: 0, y: 0 });
    }
    return next;
  });
};

const resetImageView = () => {
  setImageZoom(1);
  setImageOffset({ x: 0, y: 0 });
};

const handleImageMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
  if (imageZoom <= 1) return;
  setIsDraggingImage(true);
  setDragStart({
    x: e.clientX - imageOffset.x,
    y: e.clientY - imageOffset.y,
  });
};

const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
  if (!isDraggingImage || imageZoom <= 1) return;

  setImageOffset({
    x: e.clientX - dragStart.x,
    y: e.clientY - dragStart.y,
  });
};

const handleImageMouseUp = () => {
  setIsDraggingImage(false);
};

const handleImageWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  e.preventDefault();

  setImageZoom((prev) => {
    const next = e.deltaY < 0 ? Math.min(prev + 0.2, 4) : Math.max(prev - 0.2, 1);

    if (next === 1) {
      setImageOffset({ x: 0, y: 0 });
    }

    return next;
  });
};

const samePatientAttachment = (candidate: Pick<PatientFile, 'id' | 'storagePath' | 'url' | 'name'>, target: Pick<PatientFile, 'id' | 'storagePath' | 'url' | 'name'>) => {
  if (candidate.id && target.id && candidate.id === target.id) return true;
  if (candidate.storagePath && target.storagePath && candidate.storagePath === target.storagePath) return true;
  if (candidate.url && target.url && candidate.url === target.url) return true;
  if (!candidate.storagePath && !target.storagePath && candidate.name && target.name && candidate.name === target.name) return true;
  return false;
};

  const handleFilesSelected = async (filesInput: File[] | FileList, targetInput?: HTMLInputElement | null) => {
  try {
    const files: File[] = Array.from(filesInput);
    if (files.length === 0) return;
    const FREE_MAX_IMAGES = 3;
    const isFreePlan = userPlan === 'free';
    console.log('[PatientFiles][add:start]', {
      mode: activePatient?.id ? 'existing_patient' : 'draft',
      patientId: activePatient?.id || null,
      files: files.map((file) => ({ name: file.name, type: file.type, size: file.size })),
    });

    // =========================
    // NOVO PACIENTE (rascunho)
    // =========================
    if (!activePatient?.id) {
      const currentDraftCount = Array.isArray(formData.images) ? formData.images.length : 0;

      if (isFreePlan && currentDraftCount >= FREE_MAX_IMAGES) {
        alert(t('freePlanMaxImages'));
        if (targetInput) targetInput.value = '';
        return;
      }

      const allowedCount = isFreePlan
        ? Math.max(0, FREE_MAX_IMAGES - currentDraftCount)
        : files.length;

      const filesToUse = files.slice(0, allowedCount);

      if (isFreePlan && files.length > allowedCount) {
        alert(t('freePlanAddImagesNow', { count: allowedCount }));
      }

      for (const file of filesToUse) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setFormData((prev: any) => ({
            ...prev,
            images: [...(prev.images || []), base64],
          }));
        };
        reader.readAsDataURL(file);
      }

      showPatientUploadToast('success', t('patientFilesUploadSuccess', { count: filesToUse.length }));
      if (targetInput) targetInput.value = '';
      return;
    }

    // =========================
    // PACIENTE JÁ CRIADO
    // =========================
    const uid = auth.currentUser?.uid;
    if (!uid) {
      alert(t('unauthenticatedUser'));
      if (targetInput) targetInput.value = '';
      return;
    }

    const currentStoredCount = activePatientAttachments.length;

    if (isFreePlan && currentStoredCount >= FREE_MAX_IMAGES) {
      alert(t('freePlanMaxImages'));
      if (targetInput) targetInput.value = '';
      return;
    }

    const allowedCount = isFreePlan
      ? Math.max(0, FREE_MAX_IMAGES - currentStoredCount)
      : files.length;

    const filesToUse = files.slice(0, allowedCount);

    if (isFreePlan && files.length > allowedCount) {
      alert(t('freePlanUploadImagesNow', { count: allowedCount }));
    }

    let updatedFiles = Array.isArray((activePatient as any).patientFiles)
      ? [...(activePatient as any).patientFiles]
      : [];

    for (const file of filesToUse) {
      const uploaded = await uploadPatientFile({
        uid,
        patientId: activePatient.id,
        file,
      });

      updatedFiles = [
        uploaded,
        ...updatedFiles.filter((candidate) => !samePatientAttachment(candidate as PatientFile, uploaded)),
      ];
    }

    await updatePatient(
      activePatient.id,
      {
        patientFiles: updatedFiles,
      } as any
    );

    console.log('[PatientFiles][firestore:success]', {
      patientId: activePatient.id,
      totalFiles: updatedFiles.length,
    });

    showPatientUploadToast(
      'success',
      t('patientFilesUploadSuccess', { count: filesToUse.length })
    );

    if (targetInput) targetInput.value = '';
  } catch (err: any) {
    console.error('[PatientFiles][error]', err);
    showPatientUploadToast('error', t('patientFilesUploadError'));
    if (targetInput) targetInput.value = '';
  }
  };

// Função para fazer upload de imagem e atualizar o Firestore
const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const filesList = e.target.files;
  if (!filesList || filesList.length === 0) return;
  await handleFilesSelected(filesList, e.target);
};

  const handleAddToActivePatient = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesList = e.target.files;
    if (!filesList || filesList.length === 0) return;
    await handleFilesSelected(filesList, e.target);
  };

  const handleDeleteFromActivePatient = async (file: PatientFile) => {
  if (!activePatient?.id) return;
  if (!confirm(t("deleteImageConfirm"))) return;

  const newFiles = Array.isArray((activePatient as any).patientFiles)
    ? (activePatient as any).patientFiles.filter((candidate: PatientFile) => !samePatientAttachment(candidate, file))
    : [];
  const newImages = Array.isArray(activePatient.images)
    ? activePatient.images.filter((img) => img !== file.url)
    : [];
  await updatePatient(activePatient.id, { patientFiles: newFiles, images: newImages } as any);
};

  const handleDeleteNote = (noteId: string) => {
    if (!activePatient) return;
    if (confirm(t('deleteRecordConfirm'))) {
        const newNotes = (activePatient.clinicalNotes || []).filter(n => n.id !== noteId);
        updatePatient(activePatient.id, { clinicalNotes: newNotes });
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
        ...prev,
        images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const toggleComorbidity = (c: string) => {
    setFormData(prev => {
      const list = prev.comorbidities || [];
      if (list.includes(c)) return { ...prev, comorbidities: list.filter(i => i !== c) };
      return { ...prev, comorbidities: [...list, c] };
    });
  };

  const toggleAllergy = (a: string) => {
    setFormData(prev => {
      const list = prev.allergies || [];
      if (list.includes(a)) return { ...prev, allergies: list.filter(i => i !== a) };
      return { ...prev, allergies: [...list, a] };
    });
  };

  const handleEditPatient = () => {
    if (!activePatient) return;
    setFormData({
        name: activePatient.name,
        age: activePatient.age,
        sex: activePatient.sex,
        side: activePatient.side,
        anatomy: normalizeAnatomyValue(activePatient.anatomy),
        comorbidities: activePatientComorbidities,
        images: [],
        status: activePatient.status,
        cpf: activePatient.cpf || '',
        phone: activePatient.phone || '',
        email: activePatient.email || '',
        weight: activePatient.weight || '',
        height: activePatient.height || '',
        bmi: activePatient.bmi || '',
        chiefComplaint: activePatient.chiefComplaint || '',
        historyOfPresentIllness: activePatient.historyOfPresentIllness || '',
        painLocation: activePatient.painLocation || '',
        symptomDuration: activePatient.symptomDuration || '',
        traumaMechanism: activePatient.traumaMechanism || '',
        functionalLimitation: activePatient.functionalLimitation || '',
        physicalExam: activePatient.physicalExam || '',
        profession: activePatient.profession || '',
        suspectedDiagnosis: activePatient.suspectedDiagnosis || '',
        initialPlan: activePatient.initialPlan || '',
        notes: activePatient.notes || '',
    });
    setIsEditing(true);
    setView('form');
  };

 const handleSave = async () => {
  try {
    const normalizedAnatomy = normalizeAnatomyValue(formData.anatomy);

    // Verificar campos obrigatórios
    if (!formData.name || !normalizedAnatomy) {
      alert(t("requiredNameAnatomy"));
      return;
    }

    // Bloqueia só criação de novo paciente no plano free
    const currentDoctorInfo = doctorData
      ? {
          name: doctorData.name,
          crm: doctorData.crm,
          crmState: doctorData.crmState,
          rqe: doctorData.rqe,
          specialty: doctorData.specialty,
          subspecialty: doctorData.subspecialty,
          orthoId: doctorData.orthoId,
        }
      : undefined;

    // EDIÇÃO
    if (isEditing && activePatient) {
      await updatePatient(activePatient.id, {
        ...formData,
        anatomy: normalizedAnatomy,
        sex: formData.sex as 'M' | 'F',
        side: formData.side as Patient['side'],
        status: formData.status as any,
        doctorOrthoId: (activePatient as any).doctorOrthoId || doctorData?.orthoId,
        responsibleDoctor: currentDoctorInfo
          ? { ...(activePatient.responsibleDoctor || {}), ...currentDoctorInfo }
          : activePatient.responsibleDoctor,
      });

      setIsEditing(false);
      setView('list');
      return;
    }

    // CRIAÇÃO
    const newPatient: Patient = {
      id: Date.now().toString(),
      name: formData.name!,
      age: formData.age || '0',
      sex: formData.sex as 'M' | 'F',
      side: formData.side as Patient['side'],
      anatomy: normalizedAnatomy,
      comorbidities: formData.comorbidities || [],
      images: [],
      patientFiles: [],
      status: formData.status as any,
      createdAt: Date.now(),
      clinicalNotes: [
        {
          id: Date.now().toString(),
          date: Date.now(),
          type: 'Document',
          title: t('chartOpened'),
          content: t('patientRegistered'),
          titleKey: 'chartOpened',
          contentKey: 'patientRegistered',
        },
      ],
      cpf: formData.cpf,
      phone: formData.phone,
      email: formData.email,
      weight: formData.weight,
      height: formData.height,
      bmi: formData.bmi,
      chiefComplaint: formData.chiefComplaint,
      historyOfPresentIllness: formData.historyOfPresentIllness,
      painLocation: formData.painLocation,
      symptomDuration: formData.symptomDuration,
      traumaMechanism: formData.traumaMechanism,
      functionalLimitation: formData.functionalLimitation,
      physicalExam: formData.physicalExam,
      profession: formData.profession,
      suspectedDiagnosis: formData.suspectedDiagnosis,
      initialPlan: formData.initialPlan,
      notes: formData.notes,
      clinicalRegistry: formData.clinicalRegistry || createInitialClinicalRegistry(),
      dataSharingMode: 'private',
      doctorOrthoId: doctorData?.orthoId,
      doctor: currentDoctorInfo,
      responsibleDoctor: currentDoctorInfo,
    };

    await addPatient(newPatient);
    setActivePatient(newPatient);

    const uid = auth.currentUser?.uid;
    const draftImages: string[] = (formData.images || []).filter(
      (x: any) => typeof x === 'string' && x.startsWith('data:')
    );

    if (uid && draftImages.length > 0) {
      const uploadedMetas: PatientFile[] = [];

      for (const dataUrl of draftImages) {
        const up = await uploadPatientDataUrl({
          uid,
          patientId: newPatient.id,
          dataUrl,
        });

        uploadedMetas.push(up);
      }

      await updatePatient(newPatient.id, {
        patientFiles: uploadedMetas,
      } as any);

      setFormData((prev: any) => ({ ...prev, images: [] }));
    }

    setView('list');

    setFormData(createEmptyPatientForm());
  } catch (err: any) {
    console.error('Erro ao salvar paciente:', err);
    alert(getUsageErrorMessage(err));
  }
};
  const handleSelectPatient = (patient: Patient) => {
    setActivePatient(patient);
    setView('list');
  };

  const handleSaveCase = () => {
    if (!activePatient) return;
    setIsSaving(true);
    updatePatient(activePatient.id, { ...activePatient });
    setTimeout(() => setIsSaving(false), 1500);
  };

  const handleOpenIntelligentReport = () => {
    setShowClinicalReport(true);
    requestAnimationFrame(() => {
      intelligentReportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  function handlePrintIntelligentReport() {
    if (!activePatient) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const renderRows = (rows: Array<{ label: string; value: string }>, emptyText: string) =>
      rows.length > 0
        ? rows
            .map(
              (row) => `
                <div class="data-card">
                  <div class="label">${escapeHtml(row.label)}</div>
                  <div class="value">${escapeHtml(row.value)}</div>
                </div>
              `
            )
            .join("")
        : `<div class="empty">${escapeHtml(emptyText)}</div>`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('patientClinicalIntelligentReportTitle')} - ${escapeHtml(caseIdentifier)}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 32px;
            background: #e2e8f0;
            color: #0f172a;
            font-family: Arial, sans-serif;
          }
          .report-shell {
            max-width: 960px;
            margin: 0 auto;
            background: #ffffff;
            border: 1px solid #cbd5e1;
            border-radius: 24px;
            padding: 40px;
          }
          .report-header {
            display: flex;
            justify-content: space-between;
            gap: 24px;
            border-bottom: 2px solid #0f172a;
            padding-bottom: 20px;
            margin-bottom: 28px;
          }
          .report-title {
            font-size: 28px;
            font-weight: 700;
            margin: 0;
          }
          .report-subtitle {
            margin-top: 8px;
            font-size: 12px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #64748b;
          }
          .meta-block {
            min-width: 220px;
            text-align: right;
            font-size: 12px;
            color: #475569;
          }
          .section {
            margin-top: 24px;
          }
          .section-title {
            margin: 0 0 12px;
            font-size: 12px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #475569;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          .data-card {
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 14px 16px;
            background: #f8fafc;
          }
          .label {
            font-size: 10px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #64748b;
            margin-bottom: 6px;
          }
          .value {
            font-size: 14px;
            font-weight: 600;
            color: #0f172a;
            line-height: 1.5;
          }
          .summary-card {
            border: 1px solid #cbd5e1;
            border-radius: 18px;
            padding: 18px 20px;
            background: #f8fafc;
            color: #1e293b;
            line-height: 1.7;
            font-size: 14px;
          }
          .empty {
            border: 1px dashed #cbd5e1;
            border-radius: 14px;
            padding: 18px;
            color: #64748b;
            background: #f8fafc;
            font-size: 13px;
          }
          @media print {
            body {
              padding: 0;
              background: #ffffff;
            }
            .report-shell {
              max-width: none;
              border: none;
              border-radius: 0;
              padding: 24px;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-shell">
          <div class="report-header">
            <div>
              <h1 class="report-title">${t('patientClinicalIntelligentReportTitle')}</h1>
              <div class="report-subtitle">${t('patientStructuredCaseForPrint')}</div>
            </div>
            <div class="meta-block">
              <div><strong>${t('patientCaseId')}:</strong> ${escapeHtml(caseIdentifier)}</div>
              <div><strong>${t('patientReportDate')}:</strong> ${escapeHtml(reportDateTimeLabel)}</div>
              ${
                responsibleDoctorInfo?.name
                  ? `<div><strong>${t('patientResponsibleDoctorLower')}:</strong> ${escapeHtml(
                      responsibleDoctorInfo.name
                    )}</div>`
                  : ""
              }
              ${
                hasDisplayValue(doctorOrthoId)
                  ? `<div><strong>${t('patientOrthoIdLabel')}:</strong> ${escapeHtml(doctorOrthoId)}</div>`
                  : ""
              }
            </div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientCaseIdentification')}</h2>
            <div class="grid">
              <div class="data-card">
                <div class="label">${t('patientIdentifier')}</div>
                <div class="value">${escapeHtml(caseIdentifier)}</div>
              </div>
              <div class="data-card">
                <div class="label">${t('patientReportDate')}</div>
                <div class="value">${escapeHtml(reportDateLabel)}</div>
              </div>
              ${
                responsibleDoctorInfo?.name
                  ? `
                    <div class="data-card">
                      <div class="label">${t('patientResponsibleDoctorLower')}</div>
                      <div class="value">${escapeHtml(responsibleDoctorInfo.name)}</div>
                    </div>
                  `
                  : ""
              }
              ${
                hasDisplayValue(doctorOrthoId)
                  ? `
                    <div class="data-card">
                      <div class="label">${t('patientOrthoIdLabel')}</div>
                      <div class="value">${escapeHtml(doctorOrthoId)}</div>
                    </div>
                  `
                  : ""
              }
            </div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientDiagnosis')}</h2>
            <div class="grid">${renderRows(diagnosisRows, t('patientNoStructuredDiagnosis'))}</div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientTreatment')}</h2>
            <div class="grid">${renderRows(treatmentRows, t('patientNoStructuredTreatment'))}</div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientProcedure')}</h2>
            <div class="grid">${renderRows(procedureRows, t('patientNoStructuredProcedure'))}</div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientOutcomeEvolution')}</h2>
            <div class="grid">${renderRows(outcomeRows, t('patientNoStructuredOutcome'))}</div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientRegistryEvidence')}</h2>
            <div class="grid">${renderRows(evidenceRows, t('noRegistryComparisonData'))}</div>
          </div>

          <div class="section">
            <h2 class="section-title">${t('patientSmartSummary')}</h2>
            <div class="summary-card">${escapeHtml(intelligentSummary)}</div>
          </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  }

  function handlePrintFullReport() {
    if (!activePatient) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
  

    const doctorInfo = responsibleDoctorInfo;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('patientMedicalRecordTitle')} - ${activePatient.name}</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
           body { font-family: sans-serif; padding: 40px; }
           @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="max-w-4xl mx-auto">
           <div class="flex justify-between items-center border-b-4 border-slate-800 pb-4 mb-8">
              <div>
                 <h1 class="text-3xl font-bold text-slate-900">${t('patientIntegratedRecordTitle')}</h1>
                 <p class="text-sm text-slate-500 uppercase tracking-widest">OrthoPrecision System</p>
              </div>
              <div class="text-right">
                 <p>${t('patientDateLabel')}: ${new Date().toLocaleDateString(currentLocale)}</p>
                 <p>ID: ${activePatient.id.substring(0,8)}</p>
                 ${doctorInfo ? `<p class="text-xs font-bold text-blue-800 mt-1">${t('patientDoctorLabel')}: ${doctorInfo.name} ${doctorInfo.crm ? `(CRM: ${doctorInfo.crm})` : ''}</p>` : ''}
              </div>
           </div>

           <div class="grid grid-cols-2 gap-8 mb-8 p-6 bg-slate-50 border rounded-xl">
              <div class="col-span-2 border-b border-slate-200 pb-4 mb-4">
                 <p class="text-xs text-slate-500 uppercase">${t('patientPatientLabel')}</p>
                 <p class="text-2xl font-bold text-slate-900">${activePatient.name}</p>
                 <div class="flex gap-4 mt-1 text-sm text-slate-600">
                    ${activePatient.cpf ? `<span>CPF: ${activePatient.cpf}</span> • ` : ''}
                    <span>${activePatient.phone || ''}</span>
                    <span>${activePatient.email || ''}</span>
                 </div>
              </div>
              <div>
                 <p class="text-xs text-slate-500 uppercase">${t('patientDemographics')}</p>
                  <p class="font-bold">${t('yearsOld', { count: activePatient.age })} • ${getPatientSexLabel(activePatient.sex)} • ${getPatientStatusLabel(activePatient.status)}</p>
                 ${activePatient.bmi ? `
                    <p class="text-xs mt-1 text-slate-600">
                      ${t('patientWeight')}: ${activePatient.weight}kg • ${t('patientHeightShort')}: ${activePatient.height}m • 
                      <span class="${parseFloat(activePatient.bmi) >= 30 ? 'text-orange-600 font-bold' : ''}">${t('patientBmi')}: ${activePatient.bmi}</span>
                    </p>
                    ${parseFloat(activePatient.bmi) >= 30 ? `
                      <div class="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-[10px] text-orange-700 font-bold">
                        ⚠️ ${t('patientSafetyAlertTitle')} ${t('patientObesityRisk')}
                      </div>
                    ` : ''}
                  ` : ''}
              </div>
              <div>
                 <p class="text-xs text-slate-500 uppercase">${t('patientAnatomySide')}</p>
                 <p class="font-bold text-blue-600">${translateAnatomy(activePatient.anatomy)} (${getPatientSideFullLabel(activePatient.side)})</p>
              </div>
              <div class="col-span-2">
                 <p class="text-xs text-slate-500 uppercase">${t('comorbidities')}</p>
                 <p>${translateList(activePatientComorbidities, translateComorbidity)}</p>
              </div>
           </div>

            <h2 class="text-lg font-bold border-b border-slate-300 pb-2 mb-4">${t('patientRadiologySeries')}</h2>
            <div class="grid grid-cols-3 gap-4 mb-8">
              ${activePatientImageUrls.map(img => `
                 <div class="aspect-square border border-slate-200 bg-black flex items-center justify-center overflow-hidden rounded-lg">
                    <img src="${img}" class="w-full h-full object-contain" />
                 </div>
              `).join('')}
              ${activePatientImageUrls.length === 0 ? `<p class="text-slate-400 italic">${t('patientNoArchivedImages')}</p>` : ''}
            </div>

            <h2 class="text-lg font-bold border-b border-slate-300 pb-2 mb-4">${t('patientClinicalJourneyHistory')}</h2>
            <div class="space-y-6">
              ${activePatientClinicalNotes.length > 0 ? activePatientClinicalNotes.map(note => `
                 <div class="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <div class="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                       <span class="font-bold text-sm text-blue-800 uppercase">${getPatientNoteTypeLabel(note.type)} - ${escapeHtml(getClinicalNoteTitle(note))}</span>
                       <span class="text-xs text-slate-500">${new Date(note.date).toLocaleString(currentLocale)}</span>
                    </div>
                    <div class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">${escapeHtml(getClinicalNoteContent(note))}</div>
                 </div>
              `).join('') : `<p class="text-slate-400 text-center italic">${t('patientNoRecordsAdded')}</p>`}
            </div>

            <div class="mt-8 border border-dashed border-slate-300 rounded-xl p-8 min-h-[200px]">
              <p class="text-slate-400 text-center italic mt-10">${t('patientSupplementaryNotesSpace')}</p>
            </div>

           <div class="mt-12 pt-8 border-t-2 border-slate-900 flex flex-col items-center text-center">
  <div class="w-48 h-0.5 bg-slate-400 mb-2"></div>

  <p class="text-sm font-bold text-slate-900">
    ${doctorInfo?.name || t('patientResponsibleDoctor')}
  </p>

  <p class="text-xs text-slate-600">
    ${doctorInfo?.crm ? `CRM: ${doctorInfo.crm}` : ''}
    ${doctorInfo?.rqe ? ` • RQE: ${doctorInfo.rqe}` : ''}
  </p>

  <p class="text-[11px] text-slate-500 mt-3 font-semibold uppercase tracking-[0.18em]">
    ${t('patientOrthoIdVerified')}
  </p>

  <div class="mt-3 px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-[10px] text-slate-600 leading-relaxed">
    <p><strong>${t('patientCaseId')}:</strong> ${activePatient.id}</p>
    <p><strong>${t('patientIssuedAtLabel')}:</strong> ${new Date().toLocaleString(currentLocale)}</p>
    <p><strong>${t('patientSystemLabel')}:</strong> OrthoPrecision</p>
  </div>

  <p class="text-[10px] text-slate-400 mt-4 uppercase tracking-widest">
    ${t('patientTechnicalResponsibility')}
  </p>
</div>

           <div class="fixed bottom-4 left-0 w-full text-center text-[8px] text-slate-300 uppercase tracking-tighter">
              ${t('patientElectronicDocumentFooter', { date: new Date().toLocaleString(currentLocale) })}
           </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  }

  // Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl + N: New Patient
      if (e.ctrlKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        handleCreateNew();
      }
      // Ctrl + P: Print Report
      if (e.ctrlKey && e.key.toLowerCase() === 'p') {
        if (activePatient && view !== 'form') {
          e.preventDefault();
          handlePrintIntelligentReport();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePatient, view]);

  const getNoteIcon = (type: string) => {
    switch (type) {
        case 'Protocol': return <Activity className="w-4 h-4 text-blue-400" />;
        case 'Consult': return <BrainCircuit className="w-4 h-4 text-purple-400" />;
        case 'Atlas': return <BookOpen className="w-4 h-4 text-emerald-400" />; 
        case 'Document': return <FileSignature className="w-4 h-4 text-amber-400" />;
        default: return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  const getNoteColor = (type: string) => {
    switch (type) {
        case 'Protocol': return 'border-blue-500/30 bg-blue-500/5';
        case 'Consult': return 'border-purple-500/30 bg-purple-500/5';
        case 'Atlas': return 'border-emerald-500/30 bg-emerald-500/5';
        case 'Document': return 'border-amber-500/30 bg-amber-500/5';
        default: return 'border-slate-700 bg-slate-800/50';
    }
  };

  const patientHubTabs = [
    { key: 'evolution', label: t('patientEvolution') },
    { key: 'exams', label: t('patientFiles') },
    { key: 'surgical', label: t('patientSurgicalPlan') },
    { key: 'documents', label: t('bureaucracyTitle') },
    { key: 'ai', label: t('patientAiAnalysisDiagnosis') },
  ] as const;
  const heroExamImage = activePatientImageUrls[0] || '';
  const compactClinicalSummary =
    intelligentSummaryParts[0] ||
    t('patientNoStructuredDiagnosis');

  // --- DASHBOARD VIEW (If Active Patient Selected AND NOT in Form/Edit mode) ---
  if (activePatient && view !== 'form') {
    // Sort notes by date descending (Newest first)
    const sortedNotes = [...(activePatient.clinicalNotes || [])].sort((a, b) => b.date - a.date);

    return (
      <div className="space-y-6 animate-in slide-in-from-right-4 duration-500 relative">
        {patientUploadToast && (
          <div
            key={patientUploadToast.id}
            className={`fixed bottom-4 right-4 z-[120] max-w-sm rounded-2xl border px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.35)] backdrop-blur-md ${
              patientUploadToast.type === 'success'
                ? 'border-emerald-400/20 bg-emerald-500/15 text-emerald-50'
                : 'border-rose-400/20 bg-rose-500/15 text-rose-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 h-2.5 w-2.5 rounded-full ${patientUploadToast.type === 'success' ? 'bg-emerald-300' : 'bg-rose-300'}`} />
              <p className="text-sm leading-6">{patientUploadToast.message}</p>
            </div>
          </div>
        )}
        
        {fullScreenImage && (
  <div
    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
    onMouseMove={handleImageMouseMove}
    onMouseUp={handleImageMouseUp}
    onWheel={handleImageWheel}
  >
    {/* Top bar */}
    <div
      className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-black/70"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-white text-sm font-semibold">
        {t('patientImageViewerTitle')}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            zoomOutImage();
          }}
          className="px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 border border-slate-700"
        >
          âˆ’
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            resetImageView();
          }}
          className="px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 border border-slate-700 text-xs"
        >
          {t('patientResetView')}
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            zoomInImage();
          }}
          className="px-3 py-1.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 border border-slate-700"
        >
          +
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            closeImageViewer();
          }}
          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-500 border border-red-500"
        >
          {t('patientClose')}
        </button>
      </div>
    </div>

    {/* Viewer area */}
    <div
      className="flex-1 flex items-center justify-center overflow-hidden p-4"
      onClick={closeImageViewer}
    >
      <div
        className="relative max-w-full max-h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={fullScreenImage}
          alt={t('patientImageFullScreenAlt')}
          onMouseDown={handleImageMouseDown}
          draggable={false}
          className="max-w-[95vw] max-h-[85vh] object-contain rounded-lg shadow-2xl border border-slate-800 select-none"
          style={{
            transform: `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${imageZoom})`,
            transition: isDraggingImage ? 'none' : 'transform 0.15s ease-out',
            cursor: imageZoom > 1 ? (isDraggingImage ? 'grabbing' : 'grab') : 'zoom-in',
          }}
        />
      </div>
    </div>
  </div>
)}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,0.34fr)_minmax(0,0.66fr)]">
          <aside className="space-y-6">
            <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientDashboard')}</p>
                  <h3 className="mt-1 text-2xl font-bold text-[color:var(--op-text)]">{activePatient.name}</h3>
                  <p className="mt-2 text-sm text-[color:var(--op-text-muted)]">
                    {t('yearsOld', { count: activePatient.age })} · {activePatient.sex === 'M' ? t('male') : t('female')} · {getPatientSideShortLabel(activePatient.side)}
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${activePatient.status === 'Urgency' ? 'border border-red-500/30 bg-red-500/10 text-red-300' : 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}>
                  {getPatientStatusLabel(activePatient.status)}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-[color:var(--op-text-muted)]">
                <div className="flex items-center gap-2 rounded-2xl border border-white/8 bg-black/15 px-3 py-2.5">
                  <Stethoscope className="h-4 w-4 text-cyan-300" />
                  <span className="min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{t('patientResponsibleDoctor')}</span>
                    <span className="ml-2 font-semibold text-[color:var(--op-text)]">{responsibleDoctorInfo?.name || t('patientNotAvailable')}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 rounded-2xl border border-white/8 bg-black/15 px-3 py-2.5">
                  <Activity className="h-4 w-4 text-cyan-300" />
                  <span>{translateAnatomy(activePatient.anatomy)}</span>
                </div>
                {activePatient.bmi && (
                  <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-black/15 px-3 py-2.5">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{t('patientBmi')}</span>
                    <span className="font-semibold text-[color:var(--op-text)]">{activePatient.bmi}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientFiles')}</p>
                  <h4 className="mt-1 text-base font-semibold text-[color:var(--op-text)]">{t('patientFiles')}</h4>
                </div>
                <button
                  type="button"
                  onClick={() => activeFileInputRef.current?.click()}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10"
                >
                  {t('patientAdd')}
                </button>
                <input
                  ref={activeFileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf,.dcm,.dicom"
                  multiple
                  onChange={handleAddToActivePatient}
                />
              </div>
              <div className="mt-4 overflow-x-auto pb-2">
                <div className="flex gap-3">
                  {heroExamImage && (
                    <button
                      type="button"
                      onClick={() => setFullScreenImage(heroExamImage)}
                      className="relative h-56 w-[18rem] shrink-0 overflow-hidden rounded-[22px] border border-[color:var(--op-border)] bg-black shadow-[0_0_24px_rgba(34,211,238,0.08)]"
                    >
                      <img src={heroExamImage} alt={t('patientImageFullScreenAlt')} className="h-full w-full object-contain" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-left">
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100">{t('patientView')}</span>
                      </div>
                    </button>
                  )}
                  {activePatientAttachments.slice(0, 5).map((file, i) => {
                    const isImage = isImagePatientFile(file);
                    return (
                      <button
                        key={getPatientFileKey(file, i)}
                        type="button"
                        onClick={() => {
                          if (isImage) {
                            setFullScreenImage(file.url);
                            return;
                          }
                          window.open(file.url, '_blank', 'noopener,noreferrer');
                        }}
                        className="relative h-56 w-40 shrink-0 overflow-hidden rounded-[22px] border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)] transition hover:-translate-y-0.5 hover:border-[color:var(--op-border-strong)]"
                      >
                        {isImage ? (
                          <img src={file.url} alt={file.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center p-4 text-center">
                            <div>
                              <FileText className="mx-auto h-8 w-8 text-slate-300" />
                              <p className="mt-3 text-xs font-semibold text-slate-200">{file.name}</p>
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => activeFileInputRef.current?.click()}
                    className="flex h-56 w-40 shrink-0 items-center justify-center rounded-[22px] border border-dashed border-[color:var(--op-border)] bg-black/10 text-slate-500 transition hover:border-cyan-400/40 hover:text-cyan-200"
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs leading-5 text-[color:var(--op-text-muted)]">{t('patientImageFormatsHint')}</p>
            </div>

            <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientSmartSummary')}</p>
              <p className="mt-3 text-sm leading-6 text-[color:var(--op-text)]">{compactClinicalSummary}</p>
            </div>
          </aside>

          <section className="space-y-6">
            <div className="flex flex-col gap-3 rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-5 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)] md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActivePatient(null)}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300 transition hover:bg-white/10 hover:text-white"
                >
                  {t('backToList')}
                </button>
                <div className="hidden h-8 w-px bg-white/10 md:block" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">{t('patientClinicalJourney')}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleEditPatient}
                  className="rounded-full border border-cyan-400/15 bg-cyan-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100 transition hover:bg-cyan-500/15"
                >
                  {t('continueCase')}
                </button>
                <button
                  onClick={handlePrintIntelligentReport}
                  className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10"
                >
                  {t('generateReport')}
                </button>
                <button
                  onClick={handleSaveCase}
                  disabled={isSaving}
                  className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-100 transition hover:bg-emerald-500/15 disabled:opacity-60"
                >
                  {isSaving ? t('patientCaseSaved') : t('patientSaveCase')}
                </button>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1">
              {patientHubTabs.map((tab) => {
                const isActive = tab.key === patientHubTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setPatientHubTab(tab.key)}
                    className={`shrink-0 rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] transition ${isActive ? 'border-cyan-400/30 bg-cyan-500/15 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.08)]' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-slate-200'}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {patientHubTab === 'evolution' && (
              <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--op-text)]">
                    <Clock className="h-4 w-4 text-cyan-300" />
                    {t('patientEvolution')}
                  </h3>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                    {sortedNotes.length} {t('patientClinicalJourneyHistory')}
                  </span>
                </div>
                {sortedNotes.length === 0 ? (
                  <div className="rounded-[24px] border border-dashed border-[color:var(--op-border)] bg-black/10 px-5 py-10 text-center text-sm text-[color:var(--op-text-muted)]">
                    {t('patientNoSavedRecords')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sortedNotes.map((note, index) => (
                      <div key={note.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`flex h-10 w-10 items-center justify-center rounded-full border border-white/10 ${note.type === 'Protocol' ? 'bg-blue-500/10 text-blue-300' : note.type === 'Consult' ? 'bg-purple-500/10 text-purple-300' : note.type === 'Atlas' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-300'}`}>
                            {getNoteIcon(note.type)}
                          </div>
                          {index < sortedNotes.length - 1 && <div className="mt-2 h-full w-px bg-white/8" />}
                        </div>
                        <div className={`flex-1 rounded-[22px] border p-4 ${getNoteColor(note.type)}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{getPatientNoteTypeLabel(note.type)}</p>
                              <h4 className="mt-1 text-sm font-semibold text-[color:var(--op-text)]">{getClinicalNoteTitle(note)}</h4>
                            </div>
                            <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[10px] font-semibold text-slate-400">
                              {new Date(note.date).toLocaleString(currentLocale)}
                            </span>
                          </div>
                          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[color:var(--op-text-muted)]">
                            {getClinicalNoteContent(note)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {patientHubTab === 'exams' && (
              <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
                <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--op-text)]">
                  <BookOpen className="h-4 w-4 text-cyan-300" />
                  {t('patientFiles')}
                </h3>
                <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
                  <div className="overflow-hidden rounded-[24px] border border-[color:var(--op-border)] bg-black/20">
                    {heroExamImage ? (
                      <button type="button" onClick={() => setFullScreenImage(heroExamImage)} className="block w-full">
                        <img src={heroExamImage} alt={t('patientImageFullScreenAlt')} className="h-[420px] w-full object-contain" />
                      </button>
                    ) : (
                      <div className="flex h-[420px] items-center justify-center text-[color:var(--op-text-muted)]">{t('patientNoArchivedImages')}</div>
                    )}
                  </div>
                  <div className="overflow-y-auto rounded-[24px] border border-[color:var(--op-border)] bg-black/15 p-3">
                    <div className="grid grid-cols-2 gap-3">
                      {activePatientAttachments.map((file, i) => {
                        const isImage = isImagePatientFile(file);
                        return (
                          <button
                            key={getPatientFileKey(file, i)}
                            type="button"
                            onClick={() => {
                              if (isImage) {
                                setFullScreenImage(file.url);
                                return;
                              }
                              window.open(file.url, '_blank', 'noopener,noreferrer');
                            }}
                            className="overflow-hidden rounded-[18px] border border-[color:var(--op-border)] bg-black/20 transition hover:-translate-y-0.5 hover:border-[color:var(--op-border-strong)]"
                          >
                            {isImage ? (
                              <img src={file.url} alt={file.name} className="aspect-square w-full object-cover" />
                            ) : (
                              <div className="flex aspect-square items-center justify-center p-3 text-center">
                                <div>
                                  <FileText className="mx-auto h-7 w-7 text-slate-300" />
                                  <p className="mt-2 text-[10px] text-slate-400">{file.name}</p>
                                </div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {patientHubTab === 'surgical' && (
              <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
                <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--op-text)]">
                  <BookOpen className="h-4 w-4 text-emerald-300" />
                  {t('patientSurgicalPlan')}
                </h3>
                <p className="mt-3 text-sm leading-6 text-[color:var(--op-text-muted)]">
                  {activePatient.surgicalPlan || t('patientSurgicalPlanSubtitle')}
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <button onClick={() => onNavigateToTab(AppTab.DECISION)} className="rounded-[20px] border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-cyan-400/20 hover:bg-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientDecisionProtocols')}</p>
                    <p className="mt-1 text-sm font-semibold text-[color:var(--op-text)]">{t('patientClassificationAndConduct')}</p>
                  </button>
                  <button onClick={() => onNavigateToTab(AppTab.ATLAS)} className="rounded-[20px] border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-cyan-400/20 hover:bg-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientSurgicalPlan')}</p>
                    <p className="mt-1 text-sm font-semibold text-[color:var(--op-text)]">{t('patientSurgicalPlanSubtitle')}</p>
                  </button>
                </div>
              </div>
            )}

            {patientHubTab === 'documents' && (
              <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
                <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--op-text)]">
                  <FileSignature className="h-4 w-4 text-amber-300" />
                  {t('bureaucracyTitle')}
                </h3>
                <p className="mt-3 text-sm leading-6 text-[color:var(--op-text-muted)]">{t('patientNotesAndGuidance')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => onNavigateToTab(AppTab.BUREAUCRACY)} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10">
                    {t('generateReport')}
                  </button>
                  <button onClick={handlePrintIntelligentReport} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10">
                    {t('patientPrintReport')}
                  </button>
                </div>
                <div className="mt-4 space-y-3">
                  {activePatientClinicalNotes.filter((note) => note.type === 'Document').slice(0, 4).map((note) => (
                    <div key={note.id} className="rounded-[20px] border border-[color:var(--op-border)] bg-black/15 px-4 py-3">
                      <p className="text-sm font-semibold text-[color:var(--op-text)]">{getClinicalNoteTitle(note)}</p>
                      <p className="mt-1 text-xs text-[color:var(--op-text-muted)]">{new Date(note.date).toLocaleString(currentLocale)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {patientHubTab === 'ai' && (
              <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
                <h3 className="flex items-center gap-2 text-base font-semibold text-[color:var(--op-text)]">
                  <BrainCircuit className="h-4 w-4 text-purple-300" />
                  {t('patientAiAnalysisDiagnosis')}
                </h3>
                <p className="mt-3 text-sm leading-6 text-[color:var(--op-text-muted)]">{intelligentSummary}</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <button onClick={() => onNavigateToTab(AppTab.CONSULT)} className="rounded-[20px] border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-cyan-400/20 hover:bg-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientAiAnalysisDiagnosis')}</p>
                    <p className="mt-1 text-sm font-semibold text-[color:var(--op-text)]">{t('secondOpinionResearch')}</p>
                  </button>
                  <button onClick={handleOpenIntelligentReport} className="rounded-[20px] border border-white/10 bg-white/5 px-4 py-4 text-left transition hover:border-cyan-400/20 hover:bg-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('generateSmartReport')}</p>
                    <p className="mt-1 text-sm font-semibold text-[color:var(--op-text)]">{t('patientClinicalIntelligentReport')}</p>
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  // --- LIST VIEW ---
  if (view === 'list') {
    const filteredPatients = patients.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const activePatientCount = patients.length;
    const analysesInProgressCount = patients.filter((patient) => getPatientCaseToolProgress(patient).analysisInProgress).length;

    return (
      <PremiumPageShell className="space-y-6 animate-in fade-in duration-500 pb-10">
        <PremiumSectionCard
          title={t('patientsTitle')}
          subtitle={welcomeHeading}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <PremiumActionButton
                variant="secondary"
                icon={Mic}
                onClick={() => setShowVoiceIntakeModal(true)}
              >
                {t('patientVoiceConsultation')}
              </PremiumActionButton>
              <PremiumActionButton variant="primary" icon={Plus} onClick={handleCreateNew}>
                {t('newPatient')}
              </PremiumActionButton>
            </div>
          }
          className="p-7"
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-2xl">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--op-text-muted)]" />
              <PremiumInput
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-11"
              />
            </div>
            <div className="text-sm text-[color:var(--op-text-muted)]">
              {t('patientActivePatientsAnalyses', {
                patients: activePatientCount,
                analyses: analysesInProgressCount,
              })}
            </div>
          </div>
        </PremiumSectionCard>

        <PremiumGrid className="mx-auto max-w-[1600px] pb-4 2xl:grid-cols-3">
          {filteredPatients.map(p => (
            <PremiumModuleCard key={p.id} className="group relative cursor-pointer min-h-[235px] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-[color:var(--op-border-strong)]" onClick={() => handleSelectPatient(p)}>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--op-border-strong)] bg-gradient-to-br from-cyan-500/20 to-sky-500/10 font-bold text-white shadow-[0_0_28px_rgba(34,211,238,0.12)]">
                    {p.name.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[color:var(--op-text)]">{p.name}</h3>
                    <p className="text-xs text-[color:var(--op-text-muted)]">{t('yearsOld', { count: p.age })} • {p.sex === 'M' ? t('male') : t('female')}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${p.status === 'Urgency' ? 'border border-red-500/40 bg-red-500/12 text-red-300 shadow-[0_0_18px_rgba(239,68,68,0.12)]' : 'border border-emerald-500/40 bg-emerald-500/12 text-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.10)]'}`}>
                  {getPatientStatusLabel(p.status)}
                </span>
              </div>
              
              <div className="mb-5 space-y-3">
                <div className="flex items-center gap-2 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-3 py-2 text-xs text-[color:var(--op-text)]">
                  <Activity className="h-3 w-3 text-cyan-300" />
                  {translateAnatomy(p.anatomy)} ({getPatientSideShortLabel(p.side)})
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-white/5 pt-4 text-xs text-[color:var(--op-text-muted)]">
                <span>{new Date(p.createdAt).toLocaleDateString(currentLocale)}</span>
                {normalizePatientFiles(p.patientFiles, p.images, p.createdAt).length > 0 && (
                  <span 
                    className="relative flex cursor-help items-center gap-1 transition-colors hover:text-cyan-300"
                    onMouseEnter={(e) => {
                      setHoveredPatientImages(
                        normalizePatientFiles(p.patientFiles, p.images, p.createdAt)
                          .filter((file) => isImagePatientFile(file))
                          .map((file) => file.url)
                      );
                      setPreviewPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => setHoveredPatientImages(null)}
                  >
                    <Paperclip className="h-3 w-3" /> {t('patientFileCount', { count: normalizePatientFiles(p.patientFiles, p.images, p.createdAt).length })}
                  </span>
                )}
              </div>
              
              <button 
                onClick={(e) => { e.stopPropagation(); deletePatient(p.id); }}
                className="absolute right-3 top-3 p-2 text-slate-600 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
              >
                <X className="h-4 w-4" />
              </button>
            </PremiumModuleCard>
          ))}
          
          {/* Quick Preview Overlay */}
          {hoveredPatientImages && (
            <div 
              className="fixed z-[200] pointer-events-none animate-in fade-in zoom-in duration-200"
              style={{ 
                left: Math.min(previewPos.x + 20, window.innerWidth - 220), 
                top: Math.min(previewPos.y + 20, window.innerHeight - 220) 
              }}
            >
              <div className="flex gap-1 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)] p-2 shadow-2xl">
                {hoveredPatientImages.slice(0, 3).map((img, i) => (
                  <div key={`${img}-${i}`} className="h-20 w-20 overflow-hidden rounded-lg border border-slate-800 bg-black">
                    <img src={img} className="h-full w-full object-cover" alt={t('patientPreviewAlt')} />
                  </div>
                ))}
                {hoveredPatientImages.length > 3 && (
                  <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-slate-700 bg-slate-800 text-xs font-bold text-slate-400">
                    +{hoveredPatientImages.length - 3}
                  </div>
                )}
              </div>
            </div>
          )}

          {filteredPatients.length === 0 && (
            <div className="col-span-full">
              <PremiumEmptyState
                icon={User}
                title={t('noPatientsFound')}
                subtitle={t('startNewCase')}
                action={
                  <PremiumActionButton variant="primary" icon={Plus} onClick={handleCreateNew}>
                    {t('newPatient')}
                  </PremiumActionButton>
                }
              />
            </div>
          )}
        </PremiumGrid>
        <PatientVoiceIntakeModal
          open={showVoiceIntakeModal}
          onClose={() => setShowVoiceIntakeModal(false)}
          onApplyDraft={handleApplyVoiceDraft}
        />
      </PremiumPageShell>
    );
  }

  // --- FORM VIEW (Create New or Edit) ---
  return (
    <PremiumPageShell className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="relative overflow-hidden rounded-[32px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-6 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_24px_60px_rgba(0,0,0,0.24)] md:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.12),transparent_45%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_40%)]" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-100">
                {t('patientPreAttendanceBadge')}
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">
                {isEditing ? t('editPatient') : t('patientNewClinicalCaseTitle')}
              </span>
            </div>
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-[color:var(--op-text)] md:text-4xl">
                {isEditing ? t('editPatient') : t('patientNewClinicalCaseTitle')}
              </h2>
              <p className="max-w-3xl text-sm leading-6 text-[color:var(--op-text-muted)] md:text-base">
                {t('patientNewClinicalCaseSubtitle')}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PremiumActionButton
              variant="secondary"
              icon={Mic}
              onClick={() => setShowVoiceIntakeModal(true)}
            >
              {t('patientVoiceConsultation')}
            </PremiumActionButton>
            <button
              type="button"
              onClick={() => setView('list')}
              className="rounded-full border border-white/10 bg-white/5 p-3 text-slate-300 transition hover:bg-white/10 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <div className="space-y-6">
          <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientIdentificationCardTitle')}</p>
                <h3 className="mt-1 text-xl font-bold text-[color:var(--op-text)]">{t('patientPatientBasicInfo')}</h3>
              </div>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {t('patientVoiceConsultation')}
              </span>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('fullNamePlaceholder')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('fullNamePlaceholder')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                />
              </div>
              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('cpfPlaceholder')}</label>
                <input
                  type="text"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder={t('cpfPlaceholder')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('agePlaceholder')}</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder={t('agePlaceholder')}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientSexLabel')}</label>
                  <select
                    value={formData.sex}
                    onChange={(e) => setFormData({ ...formData, sex: e.target.value as any })}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition focus:border-cyan-400/40"
                  >
                    <option value="M">{t('patientSexMaleShort')}</option>
                    <option value="F">{t('patientSexFemaleShort')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientContactPhone')}</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder={t('phonePlaceholder')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                />
              </div>
              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientContactEmail')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('emailPlaceholder')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                />
              </div>
              <div>
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientProfession')}</label>
                <input
                  type="text"
                  value={(formData as any).profession || ''}
                  onChange={(e) => setFormData({ ...formData, profession: e.target.value } as any)}
                  placeholder={t('patientProfession')}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                />
              </div>
              <div className="md:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientWeightLabel')}</label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder={t('weightPlaceholder')}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientHeightLabel')}</label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder={t('heightPlaceholder')}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                  />
                </div>
              </div>
              {formData.bmi && (
                <div className="md:col-span-2 rounded-2xl border border-emerald-400/15 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-200">
                  {t('patientBmiLabel')}: {formData.bmi}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientClinicalContextCardTitle')}</p>
                <h3 className="mt-1 text-xl font-bold text-[color:var(--op-text)]">{t('patientClinicalContextCardTitle')}</h3>
              </div>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {t('patientLaterality')}
              </span>
            </div>
            <div className="mt-5 space-y-5">
              <div className="space-y-3">
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientLaterality')}</label>
                <div className="grid grid-cols-3 gap-2 rounded-[22px] border border-white/10 bg-white/5 p-2">
                  {([
                    { key: 'Left', label: t('patientLateralityLeft') },
                    { key: 'Right', label: t('patientLateralityRight') },
                    { key: 'Bilateral', label: t('patientLateralityBilateral') },
                  ] as const).map((option) => {
                    const active = formData.side === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setFormData({ ...formData, side: option.key })}
                        className={`rounded-[18px] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-all ${active ? 'border border-cyan-400/30 bg-cyan-500/15 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.08)]' : 'border border-transparent text-slate-400 hover:bg-white/5 hover:text-white'}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('anatomyRegion')}</label>
                <AnatomyDropdown
                  value={formData.anatomy}
                  options={anatomyOptions}
                  placeholder={t('selectOption')}
                  onChange={(value) => setFormData({ ...formData, anatomy: value })}
                  translateOption={translateAnatomy}
                />
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('comorbidities')}</label>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {comorbidityOptions.map((opt) => {
                    const active = formData.comorbidities?.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleComorbidity(opt)}
                        className={`shrink-0 rounded-full border px-3.5 py-2 text-xs font-medium transition-all ${active ? 'border-red-400/30 bg-red-500/15 text-red-100 shadow-[0_0_18px_rgba(239,68,68,0.08)]' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white'}`}
                      >
                        {translateComorbidity(opt)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientAllergies')}</label>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {allergyOptions.map((opt) => {
                    const active = formData.allergies?.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleAllergy(opt)}
                        className={`shrink-0 rounded-full border px-3.5 py-2 text-xs font-medium transition-all ${active ? 'border-amber-400/30 bg-amber-500/15 text-amber-100 shadow-[0_0_18px_rgba(251,191,36,0.08)]' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white'}`}
                      >
                        {translateAllergy(opt)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientClinicalHistoryCardTitle')}</p>
              <h3 className="mt-1 text-xl font-bold text-[color:var(--op-text)]">{t('patientConsultationAnamnesisTitle')}</h3>
              <p className="mt-2 text-sm leading-6 text-[color:var(--op-text-muted)]">{t('patientConsultationAnamnesisDescription')}</p>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                { key: 'chiefComplaint', label: t('patientChiefComplaint'), rows: 2 },
                { key: 'historyOfPresentIllness', label: t('patientHistoryOfPresentIllness'), rows: 3 },
                { key: 'painLocation', label: t('patientPainLocation'), rows: 2 },
                { key: 'symptomDuration', label: t('patientSymptomDuration'), rows: 2 },
                { key: 'traumaMechanism', label: t('patientTraumaMechanism'), rows: 2 },
                { key: 'functionalLimitation', label: t('patientFunctionalLimitation'), rows: 2 },
                { key: 'physicalExam', label: t('patientPhysicalExam'), rows: 2 },
                { key: 'suspectedDiagnosis', label: t('patientSuspectedDiagnosis'), rows: 2 },
                { key: 'initialPlan', label: t('patientInitialPlan'), rows: 2 },
              ].map((field) => (
                <div key={field.key} className={field.key === 'historyOfPresentIllness' ? 'md:col-span-2' : ''}>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{field.label}</label>
                  <textarea
                    value={(formData as any)[field.key] || ''}
                    onChange={(e) => setFormData((current) => ({ ...current, [field.key]: e.target.value } as Partial<Patient>))}
                    rows={field.rows}
                    className="w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm leading-6 text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                    placeholder={field.label}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{t('patientObservationsNotes')}</label>
                <textarea
                  value={(formData as any).notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm leading-6 text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400/40"
                  placeholder={t('patientObservationsNotesPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientExamsUploadCardTitle')}</p>
                <h3 className="mt-1 text-xl font-bold text-[color:var(--op-text)]">{t('patientExamsUploadTitle')}</h3>
                <p className="mt-2 text-sm leading-6 text-[color:var(--op-text-muted)]">{t('patientExamsUploadSubtitle')}</p>
              </div>
              <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100">
                {t('patientExamsUploadDICOMBadge')}
              </span>
            </div>
            <div
              className={`group mt-5 flex min-h-[320px] cursor-pointer flex-col overflow-hidden rounded-[28px] border border-dashed p-4 transition-all ${isPatientUploadDragging ? 'border-cyan-300/60 bg-cyan-500/10 shadow-[0_0_28px_rgba(34,211,238,0.12)]' : 'border-white/10 bg-white/5 hover:-translate-y-0.5 hover:border-cyan-400/30 hover:bg-white/[0.04] hover:shadow-[0_0_28px_rgba(34,211,238,0.08)]'}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setIsPatientUploadDragging(true);
              }}
              onDragLeave={() => setIsPatientUploadDragging(false)}
              onDrop={async (event) => {
                event.preventDefault();
                setIsPatientUploadDragging(false);
                await handleFilesSelected(Array.from(event.dataTransfer.files));
              }}
            >
              {formData.images && formData.images.length > 0 ? (
                <div className="flex h-full min-h-[270px] flex-col gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      {t('patientExamsPreviewCount', { count: formData.images.length })}
                    </span>
                    <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300">
                      {t('patientExamsUploadDICOMBadge')}
                    </span>
                  </div>
                  <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                    {formData.images.map((img, i) => (
                      <div key={i} className="group/img relative overflow-hidden rounded-[22px] border border-white/10 bg-black/30">
                        {img.startsWith('data:application/pdf') ? (
                          <div className="flex h-40 w-full items-center justify-center text-center">
                            <div>
                              <FileText className="mx-auto h-8 w-8 text-cyan-200" />
                              <p className="mt-3 text-xs font-semibold text-white">{t('consultPdfDocument')}</p>
                            </div>
                          </div>
                        ) : (
                          <img src={img} className="h-40 w-full object-contain" />
                        )}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveImage(i);
                          }}
                          className="absolute right-2 top-2 rounded-full border border-white/10 bg-black/60 p-2 text-white opacity-0 transition group-hover/img:opacity-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="flex h-40 items-center justify-center rounded-[22px] border border-dashed border-white/10 bg-white/5 text-slate-400 transition hover:border-cyan-400/40 hover:text-cyan-100"
                    >
                      <Plus className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-500/10 text-cyan-200 shadow-[0_0_28px_rgba(34,211,238,0.12)] transition-transform group-hover:scale-105">
                    <Upload className="h-10 w-10" />
                  </div>
                  <p className="mt-5 max-w-xs text-sm font-semibold text-white">{t('patientExamsUploadTitle')}</p>
                  <p className="mt-2 max-w-xs text-xs leading-5 text-slate-400">{t('patientExamsUploadHint')}</p>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200/70">{t('patientExamsUploadSubtitle')}</p>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*,application/pdf,.dcm,.dicom"
                multiple
              />
            </div>
          </div>

          <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_20px_50px_rgba(0,0,0,0.18)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('patientClinicalJourney')}</p>
                <h3 className="mt-1 text-xl font-bold text-[color:var(--op-text)]">
                  {isEditing ? t('saveChangesLabel') : t('patientSaveCase')}
                </h3>
                <p className="mt-2 text-sm leading-6 text-[color:var(--op-text-muted)]">
                  {t('patientVoiceConsultation')}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSave}
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-[20px] px-5 py-4 text-sm font-bold uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 ${isEditing ? 'border border-cyan-400/20 bg-cyan-500/15 text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,0.08)]' : 'border border-emerald-400/20 bg-emerald-500/15 text-emerald-100 shadow-[0_0_24px_rgba(16,185,129,0.08)]'}`}
              >
                {isEditing ? t('saveChangesLabel') : t('patientSaveCase')}
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setShowVoiceIntakeModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-[20px] border border-white/10 bg-white/5 px-5 py-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-200 transition-all hover:-translate-y-0.5 hover:bg-white/10"
              >
                <Mic className="h-4 w-4" />
                {t('patientVoiceConsultation')}
              </button>
            </div>
            <p className="mt-4 text-[11px] leading-5 text-slate-400">
              {t('patientExamsUploadHint')}
            </p>
          </div>
        </div>
      </div>

      <PatientVoiceIntakeModal
        open={showVoiceIntakeModal}
        onClose={() => setShowVoiceIntakeModal(false)}
        onApplyDraft={handleApplyVoiceDraft}
      />
    </PremiumPageShell>
  );
};

export default PatientModule;
